import { AlertComponent } from './components/alert/alert.component';
import { MainHeaderComponent } from './components/main-header/main-header.component';
import { AgencyHeaderComponent } from './components/agency-header/agency-header.component';
import { MainMenuComponent } from "./components/main-menu/main-menu.component";
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { AuthenticationService } from './security/authentication.service';
import { PageLoadingService } from './services/page-loading.service';
import { ErrorMessageBindingStrategy, ReactiveFormConfig } from '@rxweb/reactive-form-validators';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';
import { MainFooterComponent } from './components/main-footer/main-footer.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    RouterOutlet,
    AlertComponent,
    MainHeaderComponent,
    MainFooterComponent,
    AgencyHeaderComponent,
    MainMenuComponent,
  ]
})
export class AppComponent implements AfterViewInit, OnDestroy, OnInit {
  authenticationService = inject(AuthenticationService);
  pageLoadingService = inject(PageLoadingService);
  private activatedRoute = inject(ActivatedRoute);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private router = inject(Router);
  private titleService = inject(Title);
  private mastheadResizeObserver?: ResizeObserver;

  @ViewChild('masthead') private masthead?: ElementRef<HTMLElement>;

  defaultTitle!: string;

  ngOnInit(): void {
    void this.init();
  }

  ngAfterViewInit(): void {
    const masthead = this.masthead?.nativeElement;

    if (!masthead || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.updateMastheadHeight(masthead);
    this.mastheadResizeObserver = new ResizeObserver(() => this.updateMastheadHeight(masthead));
    this.mastheadResizeObserver.observe(masthead);
  }

  ngOnDestroy(): void {
    this.mastheadResizeObserver?.disconnect();
  }

  private init(): void {
    ReactiveFormConfig.set({
      validationMessage: {
        required: 'This field is required.',
        minLength: 'Minimum length is {{1}}',
        minDate: 'Date cannot be that far into past',
        maxLength: 'Maximum length is {{1}}',
        minNumber: 'Minimum number is {{1}}',
        maxNumber: 'Maximum number is {{1}}',
        maxDate: 'Date cannot be that far into future',
        email: 'Must be a properly formatted email address',
        zipCode: 'Zip format is ##### or #####-####',
        phone: 'Phone format is ###-###-####',
        ein: 'EIN format is ##-#######',
        url: 'Enter as https://example.com',
        range: 'Number must be between {{1}} and {{2}}',
        alpha: 'Alphabetic characters only',
        personName: 'Use letters, spaces, apostrophes, hyphens, or periods.',
        alphaNumeric: 'Alphabetic or numbers only',
        password: 'Must contain alpha and numeric characters',
        passwordLower: 'Must contain at least one lowercase letter',
        passwordUpper: 'Must contain at least one uppercase letter',
        passwordNumber: 'Must contain at least one number',
        compare: 'The value must match',
        atLeast3AlphaNumeric: 'Must contain at least 3 alphanumeric characters'
      },
      reactiveForm: {
        errorMessageBindingStrategy: ErrorMessageBindingStrategy.OnTouchedOrSubmit
      }
    });


    this.defaultTitle = this.titleService.getTitle();

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        let child = this.activatedRoute.firstChild;
        while (child?.firstChild) {
          child = child.firstChild;
        }

        const dataTitle = child?.snapshot?.data?.['title'] as string | undefined;

        if (dataTitle) {
          this.setTitle(dataTitle);
        } else {
          const segments = child?.snapshot?.url;
          if (segments?.length) {
            this.transformAndSetTitle(segments[0].path);
          } else {
            this.titleService.setTitle(this.defaultTitle);
          }
        }
      });

  }

  transformAndSetTitle(customTitle: string): void {
    customTitle = customTitle.replace(/-/g, () => ' ');
    customTitle = customTitle.replace(/\b\w/g, l => l.toUpperCase());
    this.setTitle(customTitle);
  }

  setTitle(customTitle: string): void {
    this.titleService.setTitle(`${this.defaultTitle} - ${customTitle}`);
  }

  private updateMastheadHeight(masthead: HTMLElement): void {
    this.elementRef.nativeElement.style.setProperty('--app-masthead-height', `${masthead.offsetHeight}px`);
  }
}
